import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Heart, ShieldCheck, Globe, Home as HomeIcon } from "lucide-react";
import { Button } from "../components/ui/button";
import { IMG_WAREHOUSE, IMG_SUPPORT1 } from "../mock";
import { useApp } from "../context/AppContext";
import { mediaUrl } from "../api";

export default function About() {
  const nav = useNavigate();
  const { corridors, siteImages, t } = useApp();
  const teamImg = mediaUrl(siteImages.aboutTeam) || IMG_SUPPORT1;
  const warehouseImg = mediaUrl(siteImages.warehouse) || IMG_WAREHOUSE;
  const renderHighlighted = (str) => str.split(/(\{[^}]+\})/).map((part, i) =>
    part.startsWith("{") && part.endsWith("}")
      ? <span key={i} className="orange-gradient-text">{part.slice(1, -1)}</span>
      : <React.Fragment key={i}>{part}</React.Fragment>);
  const values = [
    { icon: HomeIcon, t: "Door-to-Door Promise", d: "We never ask you to travel. Every journey starts and ends at a doorstep." },
    { icon: Heart, t: "Family First", d: "Every parcel carries love. We treat it like it's our own." },
    { icon: ShieldCheck, t: "Trust & Safety", d: "Insured, tracked and handled with the utmost care, every time." },
    { icon: Globe, t: "Truly Global", d: "Europe, the UK and key Asian corridors — all connected." },
  ];
  return (
    <div>
      <section className="relative grid-bg border-b border-black/10 overflow-hidden">
        <div className="absolute -top-24 right-1/4 w-96 h-96 rounded-full bg-orange-600/15 blur-[120px]" />
        <div className="max-w-7xl mx-auto px-5 py-20 grid lg:grid-cols-2 gap-12 items-center relative">
          <div className="animate-fade-up">
            <span className="text-orange-500 text-sm font-semibold uppercase tracking-wider">{t("abt.eyebrow")}</span>
            <h1 className="font-display font-bold text-4xl md:text-5xl mt-3 leading-tight">{renderHighlighted(t("abt.title"))}</h1>
            <p className="text-neutral-600 mt-5 text-lg leading-relaxed">
              {t("abt.p1")}
            </p>
            <p className="text-neutral-600 mt-4 leading-relaxed">
              {t("abt.p2")}
            </p>
          </div>
          <div className="rounded-3xl overflow-hidden glass p-2 orange-glow animate-fade-up" style={{ animationDelay: "0.15s" }}>
            <img src={teamImg} alt="Our team" className="rounded-2xl w-full h-[420px] object-cover" />
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 py-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {values.map((v) => (
            <div key={v.t} className="glass card-hover rounded-2xl p-7">
              <div className="w-12 h-12 rounded-xl orange-gradient grid place-items-center mb-5"><v.icon className="w-6 h-6 text-white" /></div>
              <h3 className="font-display font-semibold text-lg">{v.t}</h3>
              <p className="text-neutral-600 text-sm mt-2 leading-relaxed">{v.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 py-10 grid lg:grid-cols-2 gap-14 items-center">
        <div className="rounded-3xl overflow-hidden glass p-2"><img src={warehouseImg} alt="Warehouse" className="rounded-2xl w-full h-[360px] object-cover" /></div>
        <div>
          <span className="text-orange-500 text-sm font-semibold uppercase tracking-wider">Our Mission</span>
          <h2 className="font-display font-bold text-3xl md:text-4xl mt-3">Distance should never keep families apart</h2>
          <p className="text-neutral-600 mt-4 leading-relaxed">
            Whether it's a birthday gift from Milan to Colombo or documents from Paris to London, we make sure it arrives — safely, quickly and with love. Our corridors are designed around the routes families use most.
          </p>
          <div className="grid grid-cols-2 gap-3 mt-6">
            {corridors.map((c) => (
              <div key={c.id} className="glass rounded-xl p-4 flex items-center gap-2 text-sm">
                <span className="text-xl">{c.from}</span><ArrowRight className="w-4 h-4 text-orange-500" /><span className="text-xl">{c.to}</span>
              </div>
            ))}
          </div>
          <Button className="orange-gradient text-white border-0 mt-8 gap-2 hover:opacity-90" onClick={() => nav("/send")}>Send with us <ArrowRight className="w-4 h-4" /></Button>
        </div>
      </section>
    </div>
  );
}
